/**
 * @file LotMismatchDialog.tsx
 * @description «Партия другая: ожидалась L…, отсканирована L… — заменить?»
 *   с обязательной причиной (phase19.22, W17-9c; W18 «ручная замена с
 *   причиной»).
 *
 * Переходный период: пока остаток не промаркирован, сверка партии — только
 * предупреждение. Кладовщик может взять другую партию, но причина пишется в
 * строку задания («Замена партии L… → L…: <причина>») — без неё сервер
 * отметку не примет (`lot_mismatch_reason_required`).
 */

import { useState } from "react";

import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import type { UsePickingTaskResult } from "./usePickingTask";
import type { PickingScanLotRef } from "../../../lib/api-picking";
import { formatMonthYear } from "../../../lib/receipt-expiry";

interface Props {
  /** Состояние задания: `lotMismatch` открыт — рисуем диалог. */
  pick: UsePickingTaskResult;
  /** После решения — фокус обратно в поле скана. */
  onDone: () => void;
}

/** «L0000017 (партия RCP-…-1, до 05.2027)». */
function lotLine(lot: PickingScanLotRef): string {
  return `${lot.code} (партия ${lot.batchNumber}, до ${formatMonthYear(lot.expiresAt)})`;
}

export function LotMismatchDialog({ pick, onDone }: Props) {
  if (!pick.lotMismatch) return null;
  return (
    <LotMismatchForm
      expected={pick.lotMismatch.expected}
      scanned={pick.lotMismatch.scanned}
      busy={pick.busy}
      onConfirm={(reason) => void pick.confirmLotMismatch(reason).then(onDone)}
      onCancel={() => {
        pick.cancelLotMismatch();
        onDone();
      }}
    />
  );
}

interface FormProps {
  expected: PickingScanLotRef;
  scanned: PickingScanLotRef;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

function LotMismatchForm({ expected, scanned, busy, onConfirm, onCancel }: FormProps) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  return (
    <Dialog open onOpenChange={(open) => (!open ? onCancel() : null)}>
      {/* fullScreen="lg": планшет кладовщика работает по мобильным правилам. */}
      <DialogContent fullScreen="lg" className="lg:max-w-md">
        <DialogHeader>
          <DialogTitle>Партия другая — заменить?</DialogTitle>
          <DialogDescription>
            Ожидалась {lotLine(expected)}, отсканирована {lotLine(scanned)}.
          </DialogDescription>
        </DialogHeader>
        <label className="flex flex-col gap-1">
          <span className="label-editorial">Причина замены (обязательно)</span>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 300))}
            placeholder="Например: плановой партии нет на полке"
            className="min-h-24 text-base"
          />
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Не менять
          </Button>
          <Button onClick={() => onConfirm(trimmed)} disabled={!trimmed || busy}>
            Заменить партию
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
